import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import OfferContext from './OfferContext';
import { Offer, OfferOrigins, TiersOfferOrigins, useOffer } from '../hooks';

const OfferProvider: React.FunctionComponent<PropsWithChildren> = function ({ children }) {
    const [offers, setOffers] = useState<Array<Offer>>([]);
    const [offerOrigins, setOfferOrigins] = useState<Array<OfferOrigins>>([]);
    const [offerDataLoaded, setOfferDataLoaded] = useState<boolean>(false);
    const {
        getOffers,
        getOfferOrigins,
        getTiersOfferOrigins,
        createTiersOfferOrigin,
        updateTiersOfferOrigin,
        deleteTiersOfferOrigin,
    } = useOffer();

    const getTiersCurrentOffer = useCallback(
        (tiersId: number): Promise<Offer | undefined> =>
            new Promise((resolve, reject) => {
                getTiersOfferOrigins(tiersId)
                    .then((tiersOffresOrigines) => {
                        if (!tiersOffresOrigines[0]) {
                            resolve(undefined);

                            return;
                        }

                        const originOfferId = tiersOffresOrigines[0].origineOffreId;
                        const offerOrigin = offerOrigins.find((item) => item.id === originOfferId);
                        const offer = offers.find(
                            (item) => offerOrigin && item.id === offerOrigin.offerId,
                        );

                        resolve(offer);
                    })
                    .catch(reject);
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [offers, offerOrigins, offerDataLoaded],
    );

    const getTiersCurrentOfferWithDefault = useCallback(
        (tiersId: number): Promise<Offer | undefined> =>
            new Promise((resolve, reject) => {
                getTiersCurrentOffer(tiersId)
                    .then((offerOrigineTiers: Offer) => {
                        if (!offerOrigineTiers) {
                            const offerOrigin = offerOrigins.find((item) => item.default === true);
                            const offer = offers.find(
                                (item) => offerOrigin && item.id === offerOrigin.offerId,
                            );
                            resolve(offer);
                        }
                        resolve(offerOrigineTiers);
                    })
                    .catch(reject);
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [offers, offerOrigins, offerDataLoaded],
    );

    const setTiersCurrentOffer = useCallback(
        (tiersId: number, newOfferId?: number): Promise<TiersOfferOrigins | undefined> =>
            new Promise((resolve, reject) => {
                getTiersOfferOrigins(tiersId)
                    .then((tiersOffresOrigines) => {
                        // create a new offer for this tiers
                        if (!tiersOffresOrigines.length && newOfferId) {
                            const offerOrigin = offerOrigins.find(
                                (item) => item.offerId === newOfferId,
                            );

                            if (offerOrigin) {
                                createTiersOfferOrigin(tiersId, offerOrigin?.id)
                                    .then(resolve)
                                    .catch(reject);
                            }

                            return;
                        }

                        // replace offer for this tiers
                        if (tiersOffresOrigines.length && newOfferId) {
                            const offerOrigin = offerOrigins.find(
                                (item) => item.offerId === newOfferId,
                            );

                            if (offerOrigin) {
                                updateTiersOfferOrigin(
                                    tiersOffresOrigines[0].id,
                                    tiersId,
                                    offerOrigin?.id,
                                )
                                    .then(resolve)
                                    .catch(reject);
                            }

                            return;
                        }

                        // delete old offer for this tiers
                        if (tiersOffresOrigines.length && !newOfferId) {
                            deleteTiersOfferOrigin(tiersOffresOrigines[0].id)
                                .then(() => resolve(undefined))
                                .catch(reject);
                        }
                    })
                    .catch(reject);
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [offers, offerOrigins, offerDataLoaded],
    );

    useEffect(() => {
        const loadData = async () => {
            const [offersData, offerOriginsData] = await Promise.all([
                getOffers(),
                getOfferOrigins(),
            ]);
            setOffers(offersData);
            setOfferOrigins(offerOriginsData);
            setOfferDataLoaded(true);
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const contextValue = useMemo(
        () => ({
            offers,
            offerOrigins,
            offerDataLoaded,
            getTiersCurrentOffer,
            getTiersCurrentOfferWithDefault,
            setTiersCurrentOffer,
        }),
        [
            offers,
            offerOrigins,
            offerDataLoaded,
            getTiersCurrentOffer,
            getTiersCurrentOfferWithDefault,
            setTiersCurrentOffer,
        ],
    );

    return <OfferContext.Provider value={contextValue}>{children}</OfferContext.Provider>;
};

export default OfferProvider;
